import {
  ChatBubbleOutline,
  Close,
  Facebook,
  Instagram,
  SvgIconComponent,
  Telegram,
  WhatsApp,
} from "@mui/icons-material";
import { Box, Link, Typography, keyframes } from "@mui/material";
import React, { useEffect, useState } from "react";
// Adjust path to your actual hook location
import { useBreakpoint } from "../../../hooks/useBreakpoint";

// --- 1. TYPE DEFINITIONS & CONSTANTS -----------------------------------------

interface SocialLink {
  name: string;
  icon: SvgIconComponent;
  url: string;
  gradient: string;
  shadowColor: string; // Adds premium "colored glow"
}

// Data defined outside component for performance (prevents re-creation on render)
const SOCIAL_LINKS: SocialLink[] = [
  {
    name: "Telegram",
    icon: Telegram,
    url: "https://t.me/us2tjk",
    gradient: "linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)",
    shadowColor: "rgba(34, 158, 217, 0.5)",
  },
  {
    name: "WhatsApp",
    icon: WhatsApp,
    url: "https://wa.me/+12027606868",
    gradient: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    shadowColor: "rgba(37, 211, 102, 0.5)",
  },
  {
    name: "Facebook",
    icon: Facebook,
    url: "https://www.facebook.com/share/1F942TC2iB/?mibextid=wwXIfr",
    gradient: "linear-gradient(135deg, #1877F2 0%, #165EAB 100%)",
    shadowColor: "rgba(24, 119, 242, 0.5)",
  },
  {
    name: "Instagram",
    icon: Instagram,
    url: "https://www.instagram.com/us2tjk?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
    gradient:
      "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    shadowColor: "rgba(220, 39, 67, 0.5)",
  },
];

// Animation for Mobile Idle State
const breathAnimation = keyframes`
  0% { transform: scale(1); box-shadow: 0 4px 10px rgba(0,0,0,0.2); }
  50% { transform: scale(1.08); box-shadow: 0 8px 20px rgba(0,0,0,0.25); }
  100% { transform: scale(1); box-shadow: 0 4px 10px rgba(0,0,0,0.2); }
`;

// --- 2. COMPONENT ------------------------------------------------------------

const FloatingSocialMedia: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useBreakpoint();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // If menu is open and click target is NOT inside the card, close it.
      if (isOpen && !target.closest("#floating-social-card")) {
        setIsOpen(false);
      }
    };

    // Use 'mousedown' for quicker response than 'click'
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // --- 3. DYNAMIC STYLES -----------------------------------------------------

  // Dimensions
  const triggerSize = isMobile ? "44px" : "auto";
  const iconSize = isMobile ? 32 : 38;
  const iconFontSize = isMobile ? 18 : 22;
  const cardPadding = isMobile ? "10px" : "16px 8px";
  const gapSize = isMobile ? "10px" : "16px";

  // Positioning: Mobile = Bottom Right Floating | Desktop = Center Right Edge
  const basePosition = isMobile
    ? { bottom: "90px", right: "20px", top: "auto" }
    : { top: "50%", right: "0px", bottom: "auto" };

  // The "iOS Glass" Aesthetic Recipe
  const glassStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.65)", // Slightly more opaque for clarity
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",

    // Borders simulating light source from top
    borderTop: "1px solid rgba(255, 255, 255, 0.8)",
    borderLeft: "1px solid rgba(255, 255, 255, 0.4)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.4)",
    borderRight: isMobile ? "1px solid rgba(255, 255, 255, 0.4)" : "none",

    boxShadow: isMobile
      ? "0 8px 32px rgba(0, 0, 0, 0.2)" // Stronger shadow for floating mobile btn
      : "0 8px 32px rgba(0, 0, 0, 0.1)",
  };

  // --- 4. RENDER -------------------------------------------------------------

  return (
    <>
      {/* === TRIGGER BUTTON === */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        sx={{
          position: "fixed",
          zIndex: 1100,
          ...basePosition,
          ...glassStyle,

          // Shape Logic
          borderRadius: isMobile ? "50%" : "16px 0 0 16px",
          width: triggerSize,
          height: isMobile ? triggerSize : "auto",
          padding: isMobile ? "0" : "24px 8px",

          // Layout
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",

          // Animation Logic
          // 1. Mobile Idle: Breathe.
          // 2. Desktop/Open: Transform transition.
          animation:
            isMobile && !isOpen
              ? `${breathAnimation} 3s infinite ease-in-out`
              : "none",

          transform: isMobile
            ? "scale(1)" // Scale handled by animation keyframes
            : isOpen
              ? "translateX(100%) translateY(-50%)" // Hide to right
              : "translateX(0) translateY(-50%)", // Show

          transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)", // Springy physics

          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            animation: "none", // Stop breathing on hover
            transform: isMobile
              ? "scale(1.1)"
              : "translateX(0) translateY(-50%)",
          },
        }}
      >
        {isMobile ? (
          isOpen ? (
            <Close sx={{ color: "#1e293b", fontSize: "22px" }} />
          ) : (
            <ChatBubbleOutline sx={{ color: "#1e293b", fontSize: "22px" }} />
          )
        ) : (
          <Typography
            variant="caption"
            sx={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              fontWeight: 800,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontSize: "12px",
              color: "#334155",
              userSelect: "none",
            }}
          >
            Contact
          </Typography>
        )}
      </Box>

      {/* === EXPANDED CARD LIST === */}
      <Box
        id="floating-social-card"
        sx={{
          position: "fixed",
          zIndex: 1101,
          ...basePosition,
          ...glassStyle,
          padding: cardPadding,

          // Shape Logic
          borderRadius: isMobile ? "50px" : "20px 0 0 20px",

          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "column",
          gap: gapSize,

          // Visibility State
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? "visible" : "hidden",
          pointerEvents: isOpen ? "all" : "none",

          // Entrance Animation Direction
          transform: isOpen
            ? isMobile
              ? "translateY(-60px)" // Mobile: Slide Up
              : "translateX(0) translateY(-50%)" // Desktop: Slide In
            : isMobile
              ? "translateY(10px)" // Mobile: Slide Down (Hide)
              : "translateX(100%) translateY(-50%)", // Desktop: Slide Right (Hide)

          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        }}
      >
        {SOCIAL_LINKS.map((social) => {
          const IconComponent = social.icon;
          return (
            <Link
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.name}
              underline="none"
              sx={{
                width: iconSize,
                height: iconSize,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: social.gradient,

                // The "Colored Glow" Effect
                boxShadow: `0 4px 10px -2px ${social.shadowColor}`,

                transition: "transform 0.2s ease, box-shadow 0.2s ease",

                "&:hover": {
                  transform: "scale(1.15)",
                  boxShadow: `0 8px 20px -4px ${social.shadowColor}`,
                  zIndex: 10,
                },
              }}
            >
              <IconComponent sx={{ fontSize: iconFontSize }} />
            </Link>
          );
        })}
      </Box>
    </>
  );
};

export default FloatingSocialMedia;
